import type { Column, DefaultCellTypes, Row } from "@silevis/reactgrid";
import type {
  DeveloperListItem,
  ProjectListItem,
  WeeklyPlannerPayload,
  WeeklyPlannerRow,
} from "./api";
import {
  asReadOnly,
  ARCHIVED_ROW_CELL_CLASS,
  AVAILABLE_OVERRIDE_CELL_CLASS,
  calculateRowHeight,
  DEVELOPER_COLUMN_DRAGGING_CLASS,
  DEVELOPER_COLUMN_DROP_TARGET_LEFT_CLASS,
  DEVELOPER_COLUMN_DROP_TARGET_RIGHT_CLASS,
  COLLAB_ACTIVE_CELL_CLASS,
  displayNumber,
  EDITABLE_HOURS_CELL_CLASS,
  GROUP_HEADING_CELL_CLASS,
  HOURS_BALANCE_CELL_CLASS,
  HOURS_LEFT_CELL_CLASS,
  hoursBalanceThresholdClass,
  hoursIdentityClass,
  hoursLeftThresholdClass,
  joinClasses,
  METRIC_CELL_CLASS,
  PROJECT_COLUMN_WIDTH,
  PROJECT_ROW_DRAGGABLE_LABEL_CLASS,
  PROJECT_ROW_DRAGGING_CLASS,
  PROJECT_ROW_DROP_TARGET_BOTTOM_CLASS,
  PROJECT_ROW_DROP_TARGET_TOP_CLASS,
  PROJECT_ROW_EVEN_CELL_CLASS,
  PROJECT_ROW_ODD_CELL_CLASS,
  READONLY_HOURS_CELL_CLASS,
  ROLLUP_ROW_CELL_CLASS,
  safeNumber,
  WEEKLY_PLANNER_PANEL_HEIGHT_DEFAULT,
  WEEKLY_PLANNER_ROW_HEIGHT,
} from "@/lib/grid-styles";
import {
  buildCashFlowGrid,
  buildEffortProjectionsGrid,
  buildPaymentPlannerGrid,
  groupedProjects,
} from "@/lib/grid-builders/non-planner-builders";

export type ModuleKey =
  | "weekly_planner"
  | "my_projects"
  | "effort_projections"
  | "payment_planner"
  | "cash_flow";
export type ActiveCollaboratorCells = Record<string, string>;
export type DragRowFeedback = {
  draggingRowId?: string | null;
  dropTargetRowId?: string | null;
  dropTargetEdge?: "top" | "bottom" | null;
  draggingDeveloperId?: number | null;
  dropTargetDeveloperId?: number | null;
  dropTargetDeveloperEdge?: "left" | "right" | null;
};

export type GridContract = {
  columns: Column[];
  rows: Row<DefaultCellTypes>[];
  stickyTopRows: number;
  stickyLeftColumns: number;
  title: string;
  meta?: {
    weekStartOn?: string;
    editable?: boolean;
    plannerRowsById?: Record<
      string,
      { row_kind: "project" | "roll_up_group"; row_entity_id: number; project_group_id?: number }
    >;
    developerIdByColumnId?: Record<string, number>;
    editableHourCellKeys?: Record<string, true>;
    editableAvailableHoursCellKeys?: Record<string, true>;
    canEditProjectMetadata?: boolean;
    panelRowId?: string;
  };
};

export { WEEKLY_PLANNER_PANEL_HEIGHT_DEFAULT, WEEKLY_PLANNER_ROW_HEIGHT };

export function buildGridContract(
  moduleKey: ModuleKey,
  projects: ProjectListItem[],
  developers: DeveloperListItem[],
  weeklyPlanner: WeeklyPlannerPayload | null = null,
  openPlannerRowId: string | null = null,
  measuredPanelHeight: number | null = null,
  activeCollaboratorCells: ActiveCollaboratorCells = {},
  dragRowFeedback: DragRowFeedback = {},
): GridContract {
  switch (moduleKey) {
    case "weekly_planner":
      return weeklyPlanner
        ? buildWeeklyPlannerGrid(
            weeklyPlanner,
            openPlannerRowId,
            measuredPanelHeight ?? WEEKLY_PLANNER_PANEL_HEIGHT_DEFAULT,
            activeCollaboratorCells,
            dragRowFeedback,
          )
        : buildWeeklyPlannerGridFallback(projects, developers);
    case "my_projects":
      return weeklyPlanner
        ? buildMyProjectsGrid(
            weeklyPlanner,
            openPlannerRowId,
            measuredPanelHeight ?? WEEKLY_PLANNER_PANEL_HEIGHT_DEFAULT,
            activeCollaboratorCells,
            dragRowFeedback,
          )
        : buildMyProjectsGridFallback();
    case "effort_projections":
      return buildEffortProjectionsGrid(projects);
    case "payment_planner":
      return buildPaymentPlannerGrid(projects);
    case "cash_flow":
      return buildCashFlowGrid();
    default:
      return assertNever(moduleKey);
  }
}

function buildMyProjectsGridFallback(): GridContract {
  return {
    title: "My Projects",
    columns: [
      { columnId: "project", width: PROJECT_COLUMN_WIDTH },
      { columnId: "totalHours", width: 78 },
      { columnId: "hoursLeft", width: 78 },
      { columnId: "teamHours", width: 88 },
      { columnId: "myHours", width: 88 },
    ],
    rows: [
      {
        rowId: "header",
        height: WEEKLY_PLANNER_ROW_HEIGHT,
        cells: [
          asReadOnly({ type: "header", text: "Project", className: "rg-project-cell" }),
          asReadOnly({ type: "header", text: "Tot Hrs", className: METRIC_CELL_CLASS }),
          asReadOnly({ type: "header", text: "Hrs Left", className: METRIC_CELL_CLASS }),
          asReadOnly({ type: "header", text: "Team Hrs", className: METRIC_CELL_CLASS }),
          asReadOnly({ type: "header", text: "My Hrs", className: METRIC_CELL_CLASS }),
        ],
      },
      {
        rowId: "totals",
        height: WEEKLY_PLANNER_ROW_HEIGHT,
        cells: [
          asReadOnly({ type: "header", text: "Totals", className: "rg-project-cell" }),
          asReadOnly({ type: "text", text: "0", className: METRIC_CELL_CLASS }),
          asReadOnly({ type: "text", text: "0", className: METRIC_CELL_CLASS }),
          asReadOnly({ type: "text", text: "0", className: METRIC_CELL_CLASS }),
          asReadOnly({ type: "text", text: "0", className: METRIC_CELL_CLASS }),
        ],
      },
    ],
    stickyTopRows: 2,
    stickyLeftColumns: 5,
  };
}

function buildWeeklyPlannerGridFallback(
  projects: ProjectListItem[],
  developers: DeveloperListItem[],
): GridContract {
  const projectColumn: Column = { columnId: "project", width: 320 };
  const totalHoursColumn: Column = { columnId: "totalHours", width: 78 };
  const hoursLeftColumn: Column = { columnId: "hoursLeft", width: 78 };
  const developerColumns: Column[] = developers.map((dev) => ({
    columnId: `dev-${dev.id}`,
    width: 76,
  }));
  const totalColumn: Column = { columnId: "total", width: 88 };

  const columns = [
    projectColumn,
    totalHoursColumn,
    hoursLeftColumn,
    ...developerColumns,
    totalColumn,
  ];

  const headerCells: DefaultCellTypes[] = [
    asReadOnly({ type: "header", text: "Project", className: "rg-project-cell" }),
    asReadOnly({ type: "header", text: "Tot Hrs", className: METRIC_CELL_CLASS }),
    asReadOnly({ type: "header", text: "Hrs Left", className: METRIC_CELL_CLASS }),
    asReadOnly({ type: "header", text: "Hrs Plan", className: METRIC_CELL_CLASS }),
    ...developers.map((dev) =>
      asReadOnly({ type: "header" as const, text: dev.name.split(" ")[0] }),
    ),
  ];

  const availableRow: Row<DefaultCellTypes> = {
    rowId: "avail-hours",
    height: WEEKLY_PLANNER_ROW_HEIGHT,
    cells: [
      asReadOnly({ type: "header", text: "Avail Hrs", className: "rg-project-cell" }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      ...developers.map((dev) =>
        asReadOnly({
          type: "text" as const,
          text: displayNumber(dev.hours_available, 1),
        }),
      ),
    ],
  };

  const scheduledRow: Row<DefaultCellTypes> = {
    rowId: "scheduled-hours",
    height: WEEKLY_PLANNER_ROW_HEIGHT,
    cells: [
      asReadOnly({ type: "header", text: "Scheduled", className: "rg-project-cell" }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      ...developers.map((dev) =>
        asReadOnly({
          type: "text" as const,
          text: displayNumber(dev.hours_scheduled, 1),
        }),
      ),
    ],
  };

  const dataRows: Row<DefaultCellTypes>[] = groupedProjects(projects).flatMap((group) => {
    const groupHeader: Row<DefaultCellTypes> = {
      rowId: `group-${group.groupName}`,
      height: calculateRowHeight(group.groupName),
      cells: [
        asReadOnly({
          type: "header",
          text: group.groupName,
          className: `rg-project-cell ${GROUP_HEADING_CELL_CLASS}`,
        }),
        asReadOnly({
          type: "text",
          text: "",
          className: `${METRIC_CELL_CLASS} ${GROUP_HEADING_CELL_CLASS}`,
        }),
        asReadOnly({
          type: "text",
          text: "",
          className: `${METRIC_CELL_CLASS} ${GROUP_HEADING_CELL_CLASS}`,
        }),
        asReadOnly({
          type: "text",
          text: "",
          className: `${METRIC_CELL_CLASS} ${GROUP_HEADING_CELL_CLASS}`,
        }),
        ...developers.map(() =>
          asReadOnly({ type: "text" as const, text: "", className: GROUP_HEADING_CELL_CLASS }),
        ),
      ],
    };

    const projectRows = group.projects.map((project, projectIndex) => {
      const projectRowClass =
        projectIndex % 2 === 0 ? PROJECT_ROW_EVEN_CELL_CLASS : PROJECT_ROW_ODD_CELL_CLASS;
      const devHours: DefaultCellTypes[] = developers.map((dev) => {
        const seed = project.id * 1000 + dev.id;
        const hours = Math.floor((seed % 12) + 2); // 2-13 hours
        return { type: "number", value: hours, className: projectRowClass };
      });

      const total = devHours.reduce(
        (sum, cell) => sum + (cell.type === "number" ? cell.value || 0 : 0),
        0,
      );
      const rowLabel = `▸ ${project.name}`;

      return {
        rowId: `project-${project.id}`,
        height: calculateRowHeight(rowLabel),
        cells: [
          asReadOnly({
            type: "text",
            text: rowLabel,
            className: joinClasses("rg-project-cell", projectRowClass),
          }),
          asReadOnly({
            type: "text",
            text: displayNumber(project.harvest_total_hours, 1),
            className: joinClasses(METRIC_CELL_CLASS, projectRowClass),
          }),
          asReadOnly({
            type: "text",
            text: displayNumber(project.harvest_hours_left, 1),
            className: joinClasses(METRIC_CELL_CLASS, projectRowClass),
          }),
          asReadOnly({
            type: "text",
            text: displayNumber(total, 1),
            className: joinClasses(METRIC_CELL_CLASS, projectRowClass),
          }),
          ...devHours,
        ],
      };
    });

    return [groupHeader, ...projectRows];
  });

  return {
    title: "Weekly Portfolio Planner",
    columns,
    rows: [
      { rowId: "header", height: WEEKLY_PLANNER_ROW_HEIGHT, cells: headerCells },
      availableRow,
      scheduledRow,
      ...dataRows,
    ],
    stickyTopRows: 3,
    stickyLeftColumns: 4,
  };
}

function buildWeeklyPlannerGrid(
  weeklyPlanner: WeeklyPlannerPayload,
  openPlannerRowId: string | null,
  panelHeight: number,
  activeCollaboratorCells: ActiveCollaboratorCells,
  dragRowFeedback: DragRowFeedback,
): GridContract {
  const pastWeek = !weeklyPlanner.editable;
  const developerColumns: Column[] = weeklyPlanner.developers.map((dev) => ({
    columnId: `dev-${dev.id}`,
    width: 78,
  }));

  const columns: Column[] = [
    { columnId: "project", width: 320 },
    { columnId: "totalHours", width: 78 },
    { columnId: "hoursLeft", width: 78 },
    { columnId: "plannedHours", width: 78 },
    ...developerColumns,
  ];

  const developerIdByColumnId = weeklyPlanner.developers.reduce<Record<string, number>>(
    (acc, dev) => {
      acc[`dev-${dev.id}`] = dev.id;
      return acc;
    },
    {},
  );
  const editableHourCellKeys: Record<string, true> = {};
  const editableAvailableHoursCellKeys: Record<string, true> = {};

  const headerRow: Row<DefaultCellTypes> = {
    rowId: "header",
    height: WEEKLY_PLANNER_ROW_HEIGHT,
    cells: [
      asReadOnly({ type: "header", text: "Project", className: "rg-project-cell" }),
      asReadOnly({ type: "header", text: "Tot Hrs", className: METRIC_CELL_CLASS }),
      asReadOnly({ type: "header", text: "Hrs Left", className: METRIC_CELL_CLASS }),
      asReadOnly({
        type: "header",
        text: pastWeek ? "Act / Plan" : "Hrs Plan",
        className: METRIC_CELL_CLASS,
      }),
      ...weeklyPlanner.developers.map((dev) => {
        const className = developerHeaderDragClass(dev.id, dragRowFeedback);
        return asReadOnly({
          type: "header" as const,
          text: dev.name.split(" ")[0],
          ...(className ? { className } : {}),
        });
      }),
    ],
  };

  const availableRow: Row<DefaultCellTypes> = {
    rowId: "available",
    height: WEEKLY_PLANNER_ROW_HEIGHT,
    cells: [
      asReadOnly({ type: "header", text: "Available", className: "rg-project-cell" }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      ...weeklyPlanner.developers.map((dev) => {
        const hours =
          typeof dev.available_hours === "string"
            ? parseFloat(dev.available_hours)
            : Number(dev.available_hours ?? 0);
        const canEditAvailable = canEditAvailableHoursForUser(dev.id, weeklyPlanner);
        const cellClass = joinClasses(
          hoursIdentityClass(weeklyPlanner.current_user_id, dev.id),
          canEditAvailable ? EDITABLE_HOURS_CELL_CLASS : READONLY_HOURS_CELL_CLASS,
          dev.available_hours_manually_set ? AVAILABLE_OVERRIDE_CELL_CLASS : undefined,
        );
        if (canEditAvailable) editableAvailableHoursCellKeys[`available::dev-${dev.id}`] = true;
        if (canEditAvailable) {
          return {
            type: "number" as const,
            value: safeNumber(hours, 1),
            className: cellClass,
          };
        }

        return asReadOnly({
          type: "text" as const,
          text: displayNumber(hours, 1),
          className: cellClass,
        });
      }),
    ],
  };

  const scheduledRow: Row<DefaultCellTypes> = {
    rowId: "scheduled",
    height: WEEKLY_PLANNER_ROW_HEIGHT,
    cells: [
      asReadOnly({
        type: "header",
        text: pastWeek ? "Actual / Plan" : "Scheduled",
        className: "rg-project-cell",
      }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
      ...weeklyPlanner.developers.map((dev) => {
        const plannedHours = Number(dev.scheduled_hours ?? 0);
        const availableHours = Number(dev.available_hours ?? 0);
        const actualHours = totalActualHoursForDeveloper(weeklyPlanner, dev.id);
        return asReadOnly({
          type: "text" as const,
          text: pastWeek
            ? `${displayNumber(actualHours, 1)}/${displayNumber(plannedHours, 1)}`
            : displayNumber(plannedHours, 1),
          className: joinClasses(
            HOURS_BALANCE_CELL_CLASS,
            hoursBalanceThresholdClass(
              pastWeek ? actualHours : plannedHours,
              pastWeek ? plannedHours : availableHours,
            ),
          ),
        });
      }),
    ],
  };

  const plannerRowsById: Record<
    string,
    { row_kind: "project" | "roll_up_group"; row_entity_id: number; project_group_id?: number }
  > = {};
  const rows: Row<DefaultCellTypes>[] = [headerRow, availableRow, scheduledRow];
  let panelRowId: string | undefined;
  let rowStripeIndexWithinGroup = 0;
  let currentProjectGroupId: number | undefined;

  weeklyPlanner.rows.forEach((row) => {
    if (row.row_kind === "group_heading") {
      rowStripeIndexWithinGroup = 0;
      currentProjectGroupId = row.row_entity_id;
      rows.push(groupHeadingRow(row, weeklyPlanner.developers.length));
      return;
    }

    const rowId = plannerRowId(row.row_kind, row.row_entity_id);
    plannerRowsById[rowId] = {
      row_kind: row.row_kind,
      row_entity_id: row.row_entity_id,
      project_group_id: currentProjectGroupId,
    };

    const rowLabel = `${openPlannerRowId === rowId ? "▾" : "▸"} ${row.row_label}`;
    const isRollupRow = row.row_kind === "roll_up_group";
    const zebraClass =
      rowStripeIndexWithinGroup % 2 === 0
        ? PROJECT_ROW_EVEN_CELL_CLASS
        : PROJECT_ROW_ODD_CELL_CLASS;
    const rowClass = joinClasses(
      isRollupRow ? ROLLUP_ROW_CELL_CLASS : undefined,
      zebraClass,
      row.archived ? ARCHIVED_ROW_CELL_CLASS : undefined,
    );
    rowStripeIndexWithinGroup += 1;

    rows.push({
      rowId: rowId,
      height: calculateRowHeight(rowLabel),
      cells: [
        asReadOnly({
          type: "text",
          text: rowLabel,
          className: withCollaboratorClass(
            joinClasses(
              "rg-project-cell",
              rowClass,
              isRollupRow ? undefined : PROJECT_ROW_DRAGGABLE_LABEL_CLASS,
              projectRowDragStateClass(rowId, dragRowFeedback, row.row_kind === "project"),
            ),
            rowId,
            "project",
            activeCollaboratorCells,
          ),
        }),
        plannerMetricCell(
          row.total_hours,
          withCollaboratorClass(
            joinClasses(METRIC_CELL_CLASS, rowClass),
            rowId,
            "totalHours",
            activeCollaboratorCells,
          ),
        ),
        plannerMetricCell(
          row.hours_left,
          withCollaboratorClass(
            joinClasses(
              METRIC_CELL_CLASS,
              HOURS_LEFT_CELL_CLASS,
              hoursLeftThresholdClass(row.hours_left, row.total_hours),
              rowClass,
            ),
            rowId,
            "hoursLeft",
            activeCollaboratorCells,
          ),
        ),
        asReadOnly({
          type: "text",
          text: pastWeek
            ? `${displayNumber(totalActualHoursForRow(row), 1)}/${displayNumber(row.total_planned_hours || 0, 1)}`
            : displayNumber(row.total_planned_hours || 0, 1),
          className: withCollaboratorClass(
            joinClasses(
              METRIC_CELL_CLASS,
              pastWeek
                ? joinClasses(
                    HOURS_BALANCE_CELL_CLASS,
                    hoursBalanceThresholdClass(
                      totalActualHoursForRow(row),
                      row.total_planned_hours || 0,
                    ),
                  )
                : undefined,
              rowClass,
            ),
            rowId,
            "plannedHours",
            activeCollaboratorCells,
          ),
        }),
        ...weeklyPlanner.developers.map((developer) => {
          const canEditHours = canEditHoursForUser(row, developer.id, weeklyPlanner.editable);
          const hoursClass = joinClasses(
            rowClass,
            hoursIdentityClass(weeklyPlanner.current_user_id, developer.id),
            canEditHours ? EDITABLE_HOURS_CELL_CLASS : READONLY_HOURS_CELL_CLASS,
          );
          const cellClass = withCollaboratorClass(
            hoursClass,
            rowId,
            `dev-${developer.id}`,
            activeCollaboratorCells,
          );
          if (canEditHours) editableHourCellKeys[`${rowId}::dev-${developer.id}`] = true;

          return plannerCell(row, developer.id, canEditHours, cellClass, pastWeek);
        }),
      ],
    });

    if (openPlannerRowId === rowId) {
      panelRowId = weeklyPlannerPanelRowId(rowId);
      rows.push({
        rowId: panelRowId,
        height: panelHeight,
        cells: [
          asReadOnly({ type: "text", text: "" }),
          asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
          asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
          asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
          ...weeklyPlanner.developers.map(() => asReadOnly({ type: "text" as const, text: "" })),
        ],
      });
    }
  });

  return {
    title: "Weekly Portfolio Planner",
    columns,
    rows,
    stickyTopRows: 3,
    stickyLeftColumns: 4,
    meta: {
      weekStartOn: weeklyPlanner.week_start_on,
      editable: weeklyPlanner.editable,
      plannerRowsById,
      developerIdByColumnId,
      editableHourCellKeys,
      editableAvailableHoursCellKeys,
      canEditProjectMetadata: weeklyPlanner.permissions?.can_edit_project_metadata ?? false,
      panelRowId,
    },
  };
}

function buildMyProjectsGrid(
  weeklyPlanner: WeeklyPlannerPayload,
  openPlannerRowId: string | null,
  panelHeight: number,
  activeCollaboratorCells: ActiveCollaboratorCells,
  dragRowFeedback: DragRowFeedback,
): GridContract {
  const pastWeek = !weeklyPlanner.editable;
  const developerIdByColumnId = weeklyPlanner.developers.reduce<Record<string, number>>(
    (acc, dev) => {
      acc[`dev-${dev.id}`] = dev.id;
      return acc;
    },
    {},
  );
  const editableHourCellKeys: Record<string, true> = {};
  const plannerRowsById: Record<
    string,
    { row_kind: "project" | "roll_up_group"; row_entity_id: number; project_group_id?: number }
  > = {};
  const projectRows = weeklyPlanner.rows.filter((row) => row.row_kind === "project");
  const totals = projectRows.reduce(
    (acc, row) => {
      acc.totalHours += safeNumber(row.total_hours, 1);
      acc.hoursLeft += safeNumber(row.hours_left, 1);
      acc.teamPlanned += safeNumber(row.total_planned_hours, 1);
      acc.teamActual += safeNumber(totalActualHoursForRow(row), 1);
      weeklyPlanner.developers.forEach((developer) => {
        const cell = row.cells?.find((entry) => entry.user_id === developer.id);
        acc.perDeveloperPlanned[developer.id] =
          (acc.perDeveloperPlanned[developer.id] || 0) + safeNumber(cell?.estimated_hours, 1);
        acc.perDeveloperActual[developer.id] =
          (acc.perDeveloperActual[developer.id] || 0) + safeNumber(cell?.actual_hours, 1);
      });
      return acc;
    },
    {
      totalHours: 0,
      hoursLeft: 0,
      teamPlanned: 0,
      teamActual: 0,
      perDeveloperPlanned: {} as Record<number, number>,
      perDeveloperActual: {} as Record<number, number>,
    },
  );
  const rows: Row<DefaultCellTypes>[] = [
    {
      rowId: "header",
      height: WEEKLY_PLANNER_ROW_HEIGHT,
      cells: [
        asReadOnly({ type: "header", text: "Project", className: "rg-project-cell" }),
        asReadOnly({ type: "header", text: "Tot Hrs", className: METRIC_CELL_CLASS }),
        asReadOnly({ type: "header", text: "Hrs Left", className: METRIC_CELL_CLASS }),
        asReadOnly({
          type: "header",
          text: pastWeek ? "Act / Plan" : "Hrs Plan",
          className: METRIC_CELL_CLASS,
        }),
        ...weeklyPlanner.developers.map((dev) => {
          const className = developerHeaderDragClass(dev.id, dragRowFeedback);
          return asReadOnly({
            type: "header" as const,
            text: dev.name.split(" ")[0],
            ...(className ? { className } : {}),
          });
        }),
      ],
    },
    {
      rowId: "totals",
      height: WEEKLY_PLANNER_ROW_HEIGHT,
      cells: [
        asReadOnly({ type: "header", text: "Totals", className: "rg-project-cell" }),
        asReadOnly({
          type: "text",
          text: displayNumber(totals.totalHours, 1),
          className: METRIC_CELL_CLASS,
        }),
        asReadOnly({
          type: "text",
          text: displayNumber(totals.hoursLeft, 1),
          className: joinClasses(
            METRIC_CELL_CLASS,
            HOURS_LEFT_CELL_CLASS,
            hoursLeftThresholdClass(totals.hoursLeft, totals.totalHours),
          ),
        }),
        asReadOnly({
          type: "text",
          text: pastWeek
            ? `${displayNumber(totals.teamActual, 1)}/${displayNumber(totals.teamPlanned, 1)}`
            : displayNumber(totals.teamPlanned, 1),
          className: joinClasses(
            METRIC_CELL_CLASS,
            pastWeek
              ? joinClasses(
                  HOURS_BALANCE_CELL_CLASS,
                  hoursBalanceThresholdClass(totals.teamActual, totals.teamPlanned),
                )
              : undefined,
          ),
        }),
        ...weeklyPlanner.developers.map((developer) =>
          asReadOnly({
            type: "text",
            text: pastWeek
              ? `${displayNumber(totals.perDeveloperActual[developer.id] || 0, 1)}/${displayNumber(totals.perDeveloperPlanned[developer.id] || 0, 1)}`
              : displayNumber(totals.perDeveloperPlanned[developer.id] || 0, 1),
            className: joinClasses(
              METRIC_CELL_CLASS,
              hoursIdentityClass(weeklyPlanner.current_user_id, developer.id),
              pastWeek
                ? joinClasses(
                    HOURS_BALANCE_CELL_CLASS,
                    hoursBalanceThresholdClass(
                      totals.perDeveloperActual[developer.id] || 0,
                      totals.perDeveloperPlanned[developer.id] || 0,
                    ),
                  )
                : undefined,
            ),
          }),
        ),
      ],
    },
  ];
  let panelRowId: string | undefined;
  let rowStripeIndexWithinGroup = 0;
  let currentProjectGroupId: number | undefined;

  weeklyPlanner.rows.forEach((row) => {
    if (row.row_kind === "group_heading") {
      rowStripeIndexWithinGroup = 0;
      currentProjectGroupId = row.row_entity_id;
      rows.push({
        rowId: `group-${row.row_entity_id}`,
        height: calculateRowHeight(row.row_label),
        cells: [
          asReadOnly({
            type: "header",
            text: row.row_label,
            className: `rg-project-cell ${GROUP_HEADING_CELL_CLASS}`,
          }),
          asReadOnly({
            type: "text",
            text: "",
            className: `${METRIC_CELL_CLASS} ${GROUP_HEADING_CELL_CLASS}`,
          }),
          asReadOnly({
            type: "text",
            text: "",
            className: `${METRIC_CELL_CLASS} ${GROUP_HEADING_CELL_CLASS}`,
          }),
          asReadOnly({
            type: "text",
            text: "",
            className: `${METRIC_CELL_CLASS} ${GROUP_HEADING_CELL_CLASS}`,
          }),
          asReadOnly({
            type: "text",
            text: "",
            className: `${METRIC_CELL_CLASS} ${GROUP_HEADING_CELL_CLASS}`,
          }),
        ],
      });
      return;
    }

    if (row.row_kind !== "project") return;

    const rowId = plannerRowId(row.row_kind, row.row_entity_id);
    plannerRowsById[rowId] = {
      row_kind: row.row_kind,
      row_entity_id: row.row_entity_id,
      project_group_id: currentProjectGroupId,
    };
    const rowLabel = `${openPlannerRowId === rowId ? "▾" : "▸"} ${row.row_label}`;
    const zebraClass =
      rowStripeIndexWithinGroup % 2 === 0
        ? PROJECT_ROW_EVEN_CELL_CLASS
        : PROJECT_ROW_ODD_CELL_CLASS;
    rowStripeIndexWithinGroup += 1;
    const teamPlanned = safeNumber(row.total_planned_hours, 1);
    const teamActual = safeNumber(totalActualHoursForRow(row), 1);

    rows.push({
      rowId,
      height: calculateRowHeight(rowLabel),
      cells: [
        asReadOnly({
          type: "text",
          text: rowLabel,
          className: withCollaboratorClass(
            joinClasses(
              "rg-project-cell",
              zebraClass,
              PROJECT_ROW_DRAGGABLE_LABEL_CLASS,
              projectRowDragStateClass(rowId, dragRowFeedback, true),
            ),
            rowId,
            "project",
            activeCollaboratorCells,
          ),
        }),
        plannerMetricCell(
          row.total_hours,
          withCollaboratorClass(
            joinClasses(METRIC_CELL_CLASS, zebraClass),
            rowId,
            "totalHours",
            activeCollaboratorCells,
          ),
        ),
        plannerMetricCell(
          row.hours_left,
          withCollaboratorClass(
            joinClasses(
              METRIC_CELL_CLASS,
              HOURS_LEFT_CELL_CLASS,
              hoursLeftThresholdClass(row.hours_left, row.total_hours),
              zebraClass,
            ),
            rowId,
            "hoursLeft",
            activeCollaboratorCells,
          ),
        ),
        asReadOnly({
          type: "text",
          text: pastWeek
            ? `${displayNumber(teamActual, 1)}/${displayNumber(teamPlanned, 1)}`
            : displayNumber(teamPlanned, 1),
          className: withCollaboratorClass(
            joinClasses(METRIC_CELL_CLASS, zebraClass),
            rowId,
            "teamHours",
            activeCollaboratorCells,
          ),
        }),
        ...weeklyPlanner.developers.map((developer) => {
          const canEditHours = canEditHoursForUser(row, developer.id, weeklyPlanner.editable);
          const hoursClass = joinClasses(
            zebraClass,
            hoursIdentityClass(weeklyPlanner.current_user_id, developer.id),
            canEditHours ? EDITABLE_HOURS_CELL_CLASS : READONLY_HOURS_CELL_CLASS,
          );
          const cellClass = withCollaboratorClass(
            hoursClass,
            rowId,
            `dev-${developer.id}`,
            activeCollaboratorCells,
          );
          if (canEditHours) editableHourCellKeys[`${rowId}::dev-${developer.id}`] = true;
          return plannerCell(row, developer.id, canEditHours, cellClass, pastWeek);
        }),
      ],
    });

    if (openPlannerRowId === rowId) {
      panelRowId = weeklyPlannerPanelRowId(rowId);
      rows.push({
        rowId: panelRowId,
        height: panelHeight,
        cells: [
          asReadOnly({ type: "text", text: "" }),
          asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
          asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
          asReadOnly({ type: "text", text: "", className: METRIC_CELL_CLASS }),
          ...weeklyPlanner.developers.map(() => asReadOnly({ type: "text", text: "" })),
        ],
      });
    }
  });

  return {
    title: "My Projects",
    columns: [
      { columnId: "project", width: 320 },
      { columnId: "totalHours", width: 78 },
      { columnId: "hoursLeft", width: 78 },
      { columnId: "teamHours", width: 78 },
      ...weeklyPlanner.developers.map((dev) => ({ columnId: `dev-${dev.id}`, width: 78 })),
    ],
    rows,
    stickyTopRows: 2,
    stickyLeftColumns: 4,
    meta: {
      weekStartOn: weeklyPlanner.week_start_on,
      editable: weeklyPlanner.editable,
      plannerRowsById,
      developerIdByColumnId,
      editableHourCellKeys,
      canEditProjectMetadata: weeklyPlanner.permissions?.can_edit_project_metadata ?? false,
      panelRowId,
    },
  };
}

function plannerMetricCell(
  value: number | string | null | undefined,
  className?: string,
): DefaultCellTypes {
  if (value === null || value === undefined) {
    return asReadOnly({ type: "text", text: "", className: className || METRIC_CELL_CLASS });
  }

  return asReadOnly({
    type: "text",
    text: displayNumber(value, 1),
    className: className || METRIC_CELL_CLASS,
  });
}

function groupHeadingRow(row: WeeklyPlannerRow, developerCount: number): Row<DefaultCellTypes> {
  return {
    rowId: `group-${row.row_entity_id}`,
    height: calculateRowHeight(row.row_label),
    cells: [
      asReadOnly({
        type: "header",
        text: row.row_label,
        className: `rg-project-cell ${GROUP_HEADING_CELL_CLASS}`,
      }),
      asReadOnly({
        type: "text",
        text: "",
        className: `${METRIC_CELL_CLASS} ${GROUP_HEADING_CELL_CLASS}`,
      }),
      asReadOnly({
        type: "text",
        text: "",
        className: `${METRIC_CELL_CLASS} ${GROUP_HEADING_CELL_CLASS}`,
      }),
      asReadOnly({
        type: "text",
        text: "",
        className: `${METRIC_CELL_CLASS} ${GROUP_HEADING_CELL_CLASS}`,
      }),
      ...new Array(developerCount)
        .fill(null)
        .map(() =>
          asReadOnly({ type: "text" as const, text: "", className: GROUP_HEADING_CELL_CLASS }),
        ),
    ],
  };
}

function plannerCell(
  row: WeeklyPlannerRow,
  userId: number,
  editable: boolean,
  rowClass?: string,
  historicalReadOnly = false,
): DefaultCellTypes {
  const cell = row.cells?.find((entry) => entry.user_id === userId);
  const estimated = safeNumber(cell?.estimated_hours, 1);
  const actual = safeNumber(cell?.actual_hours, 1);

  if (editable) {
    return { type: "number", value: estimated, className: rowClass };
  }

  return asReadOnly({
    type: "text",
    text: `${actual}/${estimated}`,
    className: joinClasses(
      rowClass,
      historicalReadOnly
        ? joinClasses(HOURS_BALANCE_CELL_CLASS, hoursBalanceThresholdClass(actual, estimated))
        : undefined,
    ),
  });
}

function canEditHoursForUser(
  row: WeeklyPlannerRow,
  userId: number,
  editableWeek: boolean,
): boolean {
  if (!editableWeek) return false;
  if (row.can_edit_all_hours) return true;

  const editableUserIds = row.editable_user_ids || [];
  return editableUserIds.includes(userId);
}

function canEditAvailableHoursForUser(
  userId: number,
  weeklyPlanner: WeeklyPlannerPayload,
): boolean {
  if (!weeklyPlanner.editable) return false;
  if (weeklyPlanner.permissions?.can_edit_project_metadata) return true;

  return weeklyPlanner.current_user_id === userId;
}

function totalActualHoursForDeveloper(
  weeklyPlanner: WeeklyPlannerPayload,
  developerId: number,
): number {
  return weeklyPlanner.rows.reduce((sum, row) => {
    if (row.row_kind === "group_heading" || !row.cells) return sum;
    const cell = row.cells.find((entry) => entry.user_id === developerId);
    return sum + safeNumber(cell?.actual_hours, 1);
  }, 0);
}

function totalActualHoursForRow(row: WeeklyPlannerRow): number {
  if (!row.cells) return 0;
  return row.cells.reduce((sum, cell) => sum + safeNumber(cell.actual_hours, 1), 0);
}

function withCollaboratorClass(
  baseClass: string | undefined,
  rowId: string,
  columnId: string,
  activeCollaboratorCells: ActiveCollaboratorCells,
): string | undefined {
  const key = `${rowId}::${columnId}`;
  if (!activeCollaboratorCells[key]) return baseClass;

  return joinClasses(baseClass, COLLAB_ACTIVE_CELL_CLASS);
}

function developerHeaderDragClass(
  developerId: number,
  dragRowFeedback: DragRowFeedback,
): string | undefined {
  if (developerId === dragRowFeedback.draggingDeveloperId) return DEVELOPER_COLUMN_DRAGGING_CLASS;
  if (developerId !== dragRowFeedback.dropTargetDeveloperId) return undefined;

  return dragRowFeedback.dropTargetDeveloperEdge === "right"
    ? DEVELOPER_COLUMN_DROP_TARGET_RIGHT_CLASS
    : DEVELOPER_COLUMN_DROP_TARGET_LEFT_CLASS;
}

function projectRowDragStateClass(
  rowId: string,
  dragRowFeedback: DragRowFeedback,
  canShowDragState: boolean,
): string | undefined {
  if (!canShowDragState) return undefined;
  if (rowId === dragRowFeedback.draggingRowId) return PROJECT_ROW_DRAGGING_CLASS;
  if (rowId !== dragRowFeedback.dropTargetRowId) return undefined;

  return dragRowFeedback.dropTargetEdge === "bottom"
    ? PROJECT_ROW_DROP_TARGET_BOTTOM_CLASS
    : PROJECT_ROW_DROP_TARGET_TOP_CLASS;
}

function plannerRowId(rowKind: "project" | "roll_up_group", rowEntityId: number): string {
  return `planner-${rowKind}-${rowEntityId}`;
}

export function weeklyPlannerPanelRowId(openRowId: string): string {
  return `panel-${openRowId}`;
}

function assertNever(value: never): never {
  throw new Error(`Unknown module key: ${String(value)}`);
}
